import { getActiveProfile, getProfileById } from './printerProfileService';
import type { PrinterProfile } from '../types/printerProfile';

/**
 * v5 — MULTI-IMPRIMANTES : résolution du module (profil) PAR imprimante.
 *
 * La console Restomi n'a qu'UN profil actif global. Le kiosk ajoute ici la possibilité
 * d'associer un profil DIFFÉRENT à chaque imprimante (client vs cuisine), avec repli sur
 * le profil actif global si rien n'est configuré → aucune régression.
 *
 * ⚠️ Deux garde-fous repris de la console :
 *  - CUISINE toujours en TEXTE (jamais raster/image) : géré à la composition du ticket.
 *  - Une imprimante Star/Sunmi ne comprend pas la balise <img> → si on lui a demandé un
 *    profil raster, on bascule CETTE imprimante sur un profil texte.
 */

/** Types natifs qui NE comprennent PAS l'image <img> (hexa imprimé en clair sinon). */
const RASTER_INCOMPATIBLE_TYPES = ['sunmi', 'star_usb', 'star'];

/**
 * Type natif déduit de l'ID d'imprimante, construit côté Android sous la forme
 * `type + "-" + adresse` (ex. `usb-/dev/bus/usb/001/002`, `star_usb-/dev/...`).
 * Les types composés utilisent un souligné (`star_usb`), jamais un tiret → découpe au 1er `-`.
 */
export const printerTypeOf = (printerId: unknown): string =>
  String(printerId ?? '').split('-')[0].trim().toLowerCase();

/** Un profil est-il de type image (raster) ? */
export const isRasterProfile = (profile: PrinterProfile | null | undefined): boolean =>
  !!profile && (profile.type === 'raster' || profile.type === 'raster-hybrid');

/**
 * Résout le profil VOULU pour une imprimante :
 *  - si un profileId est mappé → ce profil
 *  - sinon → le profil actif global (repli, comportement historique)
 */
export const profileFor = async (
  mappedProfileId?: string
): Promise<PrinterProfile | null> => {
  if (mappedProfileId) {
    const p = await getProfileById(mappedProfileId);
    if (p) return p;
  }
  return await getActiveProfile();
};

/**
 * Profil EFFECTIF pour une imprimante : si le profil voulu est raster mais que
 * l'imprimante ne sait pas afficher d'image (Star/Sunmi), on bascule sur un profil texte
 * pour CETTE imprimante seulement. Sinon on garde le profil voulu.
 */
export const effectiveProfileFor = (
  printerId: string,
  wanted: PrinterProfile | null,
  textProfile: PrinterProfile | null
): PrinterProfile | null => {
  if (!wanted) return textProfile;
  if (isRasterProfile(wanted) && RASTER_INCOMPATIBLE_TYPES.includes(printerTypeOf(printerId))) {
    return textProfile || wanted;
  }
  return wanted;
};
